/**
 * How an import job's status reads to an operator.
 *
 * The console has one chip component (CRMStatusChip) and one colour vocabulary, keyed on a fixed
 * set of status words. The import worker's words (queued/running/done/blocked/canceled) are not in
 * that set, so each is mapped to the chip key whose colour already means the right thing, with the
 * import word kept as the visible label. This borrows the colour system rather than inventing a
 * second one, which is what guardrail 2 and the single-chip rule exist to prevent.
 *
 * `tone` is the same meaning for the surfaces a chip cannot reach — the stacked bar segment and the
 * row accent — resolved to a semantic token in the stylesheet.
 */
import type { JobStatus } from './importApi';

export type StatusTone = 'neutral' | 'info' | 'progress' | 'success' | 'warning' | 'danger';

interface StatusDisplay {
  /** The visible word. */
  label: string;
  /** The CRMStatusChip key whose colour this status should take. */
  chipKey: string;
  /** The semantic tone, for the stacked bar and row accent. */
  tone: StatusTone;
}

export const JOB_STATUS_DISPLAY: Record<JobStatus, StatusDisplay> = {
  // Queued is waiting to be picked up: amber, like a pending approval.
  queued: { label: 'Queued', chipKey: 'pending', tone: 'info' },
  // Running is in progress: the lead blue, which reads as "being worked".
  running: { label: 'Running', chipKey: 'new', tone: 'progress' },
  done: { label: 'Done', chipKey: 'success', tone: 'success' },
  failed: { label: 'Failed', chipKey: 'failed', tone: 'danger' },
  // Skipped is a deliberate no-op (already imported, nothing to do): amber, not red.
  skipped: { label: 'Skipped', chipKey: 'skipped', tone: 'warning' },
  // Blocked is a refusal (safety or policy): red, like a rejection, but its own word.
  blocked: { label: 'Blocked', chipKey: 'rejected', tone: 'danger' },
  // Canceled is an operator stopping the batch: grey, finished and not an error.
  canceled: { label: 'Canceled', chipKey: 'inactive', tone: 'neutral' },
};

export function statusLabel(status: string): string {
  return JOB_STATUS_DISPLAY[status as JobStatus]?.label ?? status;
}

export function statusChipKey(status: string): string {
  return JOB_STATUS_DISPLAY[status as JobStatus]?.chipKey ?? status;
}

export function statusTone(status: string): StatusTone {
  return JOB_STATUS_DISPLAY[status as JobStatus]?.tone ?? 'neutral';
}
